import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Activity,
  Battery,
  Calendar,
  Cpu,
  Hospital,
  Plus,
  RefreshCw,
  Users,
  Video,
  Stethoscope,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import WalkinModal from "../components/WalkinModal";

function KpiCard({ label, value, icon: Icon, accent }) {
  const testid = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return (
    <div
      data-testid={`clinic-kpi-${testid}`}
      className="bg-white border border-border rounded-2xl p-5 shadow-sm flex items-center gap-4"
    >
      <span className={"h-11 w-11 rounded-xl flex items-center justify-center " + (accent || "bg-accent text-accent-foreground")}>
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold tracking-tight mt-0.5">{value}</div>
      </div>
    </div>
  );
}

const STATUS_DOT = {
  available: "bg-emerald-500",
  in_use: "bg-amber-500",
  offline: "bg-zinc-400",
};

export default function ClinicDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [walkinOpen, setWalkinOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, dv] = await Promise.all([
        api.get("/clinic/me/dashboard"),
        api.get("/clinic/me/devices"),
      ]);
      setData(d.data);
      setDevices(dv.data.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const reconnect = async (id) => {
    try {
      await api.post(`/clinic/me/devices/${id}/reconnect`);
      toast.success("Device reconnected");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col">
      <Navbar />
      <main
        data-testid="clinic-dashboard"
        className="flex-1 px-6 lg:px-10 py-8 lg:py-10 max-w-7xl w-full mx-auto space-y-8"
      >
        <section className="bg-primary text-primary-foreground rounded-3xl p-7 sm:p-9 relative overflow-hidden shadow-sm">
          <div className="absolute -right-12 -top-12 h-56 w-56 rounded-full bg-white/10 blur-2xl" aria-hidden />
          <div className="relative">
            <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-80 mb-3">
              Clinic · Healthink
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Front-desk console</h1>
            <p className="mt-2 text-sm sm:text-base opacity-90">
              {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </p>
            <div className="mt-5">
              <Button
                data-testid="clinic-walkin-open-btn"
                onClick={() => setWalkinOpen(true)}
                className="rounded-full bg-foreground text-background hover:bg-foreground/90 hover:text-background"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Add walk-in patient
              </Button>
            </div>
          </div>
        </section>

        {loading || !data ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 bg-muted rounded-2xl animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label="Today's appts" value={data.today_appointments_count} icon={Calendar} />
            <KpiCard label="Walk-ins today" value={data.walkins_today} icon={Users} />
            <KpiCard label="Tests running" value={data.pending_tests} icon={Activity} accent="bg-amber-50 text-amber-700" />
            <KpiCard label="Online devices" value={data.online_devices} icon={Cpu} accent="bg-emerald-50 text-emerald-700" />
          </div>
        )}

        {/* Today's schedule */}
        <section className="space-y-3">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Today's schedule</h2>
              <p className="text-sm text-muted-foreground mt-0.5">All appointments at this clinic today.</p>
            </div>
            <Button variant="outline" onClick={() => navigate("/clinic/appointments")} className="rounded-full">
              All appointments
            </Button>
          </div>
          {(data?.todays_schedule || []).length === 0 ? (
            <div data-testid="clinic-empty-today" className="bg-white border border-border rounded-2xl p-10 text-center">
              <Hospital className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <div className="font-semibold text-lg mb-1">No appointments today</div>
              <div className="text-sm text-muted-foreground">Add a walk-in or wait for online bookings.</div>
            </div>
          ) : (
            <div className="space-y-2">
              {data.todays_schedule.map((a) => (
                <div
                  key={a.id}
                  data-testid={`clinic-today-row-${a.id}`}
                  onClick={() => navigate(`/clinic/appointments/${a.id}/checkups`)}
                  className="bg-white border border-border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer hover:border-primary/50 hover:shadow-sm transition-all"
                >
                  <div className="text-center w-20 shrink-0">
                    <div className="text-xs text-muted-foreground uppercase">Time</div>
                    <div className="font-semibold">{new Date(a.scheduled_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" })}</div>
                  </div>
                  <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Patient</div>
                      <div className="font-medium">{a.patient?.name} {a.walkin && <span className="text-[10px] uppercase tracking-wider ml-1 bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">walk-in</span>}</div>
                      <div className="text-xs text-muted-foreground">{a.patient?.phone}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Doctor</div>
                      <div className="font-medium">{a.doctor?.name}</div>
                      <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                        {a.mode === "video" ? <Video className="h-3 w-3" /> : <Stethoscope className="h-3 w-3" />}
                        {a.mode}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {a.mode === "video" && a.status !== "completed" && (
                      <Button
                        data-testid={`clinic-row-join-${a.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/clinic/consult/${a.id}`);
                        }}
                        variant="outline"
                        className="rounded-full border-primary text-primary hover:bg-primary/10 gap-1.5"
                      >
                        <Video className="h-3.5 w-3.5" />
                        Join Call
                      </Button>
                    )}
                    {a.status === "completed" ? (
                      <Button
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/clinic/appointments/${a.id}/checkups`);
                        }}
                        className="text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                      >
                        Completed · Checkups
                      </Button>
                    ) : (
                      <Button
                        data-testid={`clinic-row-checkups-${a.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/clinic/appointments/${a.id}/checkups`);
                        }}
                        className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
                      >
                        Checkups
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Devices */}
        <section className="space-y-3">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">Devices</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Bluetooth diagnostic devices paired with this clinic.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {devices.map((d) => (
              <div key={d.id} data-testid={`clinic-device-${d.id}`} className="bg-white border border-border rounded-2xl p-4 flex items-center gap-3">
                <span className={"h-2.5 w-2.5 rounded-full shrink-0 " + (STATUS_DOT[d.status] || "bg-zinc-300")} aria-hidden />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{d.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{d.supported_tests.join(" · ")}</div>
                </div>
                <div className="text-xs inline-flex items-center gap-1 text-muted-foreground">
                  <Battery className="h-3.5 w-3.5" /> {d.battery_percent}%
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`clinic-device-reconnect-${d.id}`}
                  onClick={() => reconnect(d.id)}
                  className="rounded-full"
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reconnect
                </Button>
              </div>
            ))}
          </div>
        </section>
      </main>

      {walkinOpen && (
        <WalkinModal
          open={walkinOpen}
          onClose={() => setWalkinOpen(false)}
          onSuccess={() => { setWalkinOpen(false); load(); }}
        />
      )}
    </div>
  );
}
